import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAmplitude } from 'contexts';
import { AmplitudeEvents } from 'constants/amplitudeEvents';
import type { SearchResultHit } from '../types';
import type { GlobalSearchEntityType } from 'generated/graphql';

type UseSearchOverlayAmplitudeTrackingOptions = {
  open: boolean;
  query: string;
  searchTerm: string;
  selectedLocationId: string | null;
  selectedCategory: GlobalSearchEntityType | null;
  loading: boolean;
  error?: unknown;
  searchResults?: SearchResultHit[];
  displayedResultsCount: number;
  displayedResultsSearchTerm?: string;
};

type SearchOverlayAmplitudePayload = {
  platform: 'web';
  search_source: 'modal';
  search_term: string;
  filters_applied: string[];
  selected_category?: GlobalSearchEntityType;
  results_count?: number;
  latency_ms?: number;
  result_position?: number;
  result_type?: string;
};

const PLATFORM = 'web';
const SEARCH_SOURCE = 'modal';

const getSearchKey = (
  searchTerm: string,
  selectedLocationId: string | null,
  selectedCategory: GlobalSearchEntityType | null
) =>
  JSON.stringify({
    searchTerm,
    selectedLocationId,
    selectedCategory,
  });

const getLatency = (startedAt: number | undefined): number | undefined => {
  if (startedAt === undefined) {
    return undefined;
  }
  return Math.round(performance.now() - startedAt);
};

const getResultType = (result: SearchResultHit): string => {
  switch (result.entityType) {
    case 'USER':
      return 'user';
    case 'DESK':
      return 'desk';
    case 'SPACE':
      return 'space';
    default:
      return String(result.entityType).toLowerCase();
  }
};

export const useSearchOverlayAmplitudeTracking = ({
  open,
  query,
  searchTerm,
  selectedLocationId,
  selectedCategory,
  loading,
  error,
  searchResults,
  displayedResultsCount,
  displayedResultsSearchTerm,
}: UseSearchOverlayAmplitudeTrackingOptions) => {
  const { trackEvent } = useAmplitude();
  const submittedSearchKeyRef = useRef<string | null>(null);
  const completedSearchKeyRef = useRef<string | null>(null);
  const searchStartedAtByKeyRef = useRef<Map<string, number>>(new Map());

  const filtersApplied = useMemo(() => {
    const filters: string[] = [];

    if (selectedLocationId) {
      filters.push(`location_id:${selectedLocationId}`);
    }

    if (selectedCategory) {
      filters.push(`entity_type:${selectedCategory}`);
    }

    return filters;
  }, [selectedLocationId, selectedCategory]);

  const buildPayload = useCallback(
    (
      eventSearchTerm: string,
      extraMetadata: Partial<SearchOverlayAmplitudePayload> = {}
    ): SearchOverlayAmplitudePayload => ({
      platform: PLATFORM,
      search_source: SEARCH_SOURCE,
      search_term: eventSearchTerm,
      filters_applied: filtersApplied,
      ...(selectedCategory ? { selected_category: selectedCategory } : {}),
      ...extraMetadata,
    }),
    [filtersApplied, selectedCategory]
  );

  const searchKey =
    open && searchTerm.length >= 2
      ? getSearchKey(searchTerm, selectedLocationId, selectedCategory)
      : null;

  useEffect(() => {
    if (!searchKey) {
      submittedSearchKeyRef.current = null;
      completedSearchKeyRef.current = null;
      return;
    }

    if (submittedSearchKeyRef.current === searchKey) {
      return;
    }

    submittedSearchKeyRef.current = searchKey;
    completedSearchKeyRef.current = null;
    searchStartedAtByKeyRef.current.set(searchKey, performance.now());
    trackEvent(AmplitudeEvents.SEARCH_SUBMITTED, buildPayload(searchTerm));
  }, [buildPayload, searchKey, searchTerm, trackEvent]);

  useEffect(() => {
    if (
      !searchKey ||
      loading ||
      error ||
      !searchResults ||
      completedSearchKeyRef.current === searchKey
    ) {
      return;
    }

    const resultsCount = searchResults.length;
    const latency = getLatency(searchStartedAtByKeyRef.current.get(searchKey));
    searchStartedAtByKeyRef.current.delete(searchKey);
    completedSearchKeyRef.current = searchKey;

    if (resultsCount === 0) {
      trackEvent(
        AmplitudeEvents.SEARCH_NO_RESULTS,
        buildPayload(searchTerm, {
          results_count: resultsCount,
          latency_ms: latency,
        })
      );
      return;
    }

    trackEvent(
      AmplitudeEvents.SEARCH_RESULTS_VIEWED,
      buildPayload(searchTerm, {
        results_count: resultsCount,
        latency_ms: latency,
      })
    );
  }, [
    buildPayload,
    error,
    loading,
    searchKey,
    searchResults,
    searchTerm,
    trackEvent,
  ]);

  const trackSearchCleared = useCallback(() => {
    trackEvent(
      AmplitudeEvents.SEARCH_CLEARED,
      buildPayload(query, {
        results_count: displayedResultsCount,
      })
    );
  }, [buildPayload, displayedResultsCount, query, trackEvent]);

  const trackSearchCanceled = useCallback(() => {
    trackEvent(
      AmplitudeEvents.SEARCH_CANCELED,
      buildPayload(query, {
        results_count: displayedResultsCount,
      })
    );
  }, [buildPayload, displayedResultsCount, query, trackEvent]);

  const trackSearchResultClicked = useCallback(
    (result: SearchResultHit, index: number) => {
      trackEvent(
        AmplitudeEvents.SEARCH_RESULT_CLICKED,
        buildPayload(displayedResultsSearchTerm || '', {
          results_count: displayedResultsCount,
          result_position: index + 1,
          result_type: getResultType(result),
        })
      );
    },
    [
      buildPayload,
      displayedResultsCount,
      displayedResultsSearchTerm,
      trackEvent,
    ]
  );

  return {
    trackSearchCleared,
    trackSearchCanceled,
    trackSearchResultClicked,
  };
};
